import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { TextButtonCell, TooltipTextCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR,
  DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR,
} from '~/constants';

import { StatusBadge, joinOrDash } from './deliveryAgencyTableCells';

import type { DeliveryResultReportItem } from '~/types/delivery-agency.types';

import styles from './getDeliveryAgencyReportsColumnDefs.module.scss';

const getDeliveryAgencyReportsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (report: DeliveryResultReportItem) => void;
}) => {
  const columnDefs: TypeTableColumn<DeliveryResultReportItem>[] = [
    {
      id: 'rowNumber',
      header: 'No',
      cell: ({ row }) => row.index + 1 + currentPage * pageSize,
      size: 72,
    },
    {
      accessorKey: 'outboundCode',
      header: '출고번호',
      size: 160,
    },
    {
      accessorKey: 'clientOrderCodes',
      header: '판매처 주문번호',
      size: 160,
      cell: ({ row }) => {
        const { clientOrderCodes } = row.original;
        return <TooltipTextCell valueFormatted={joinOrDash(clientOrderCodes)} />;
      },
    },
    {
      accessorKey: 'shippingLabelNumber',
      header: '운송장 번호',
      size: 232,
    },
    {
      id: 'viewDetail',
      header: '',
      minSize: 80,
      size: 80,
      cell: ({ row }) => (
        <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original)}>
          상세보기
        </TextButtonCell>
      ),
    },
    {
      accessorKey: 'estimatedDeliveryDate',
      header: '배송예정일',
      size: 100,
      cell: ({ row }) => formatDate(row.original.estimatedDeliveryDate, DATE_FORMAT.DATE) ?? '-',
    },
    {
      accessorKey: 'deliveryType',
      header: '배송 유형',
      size: 96,
      cell: ({ row }) => row.original.deliveryType?.description || '-',
    },
    {
      accessorKey: 'reportedShippingLabelStatus',
      header: '운송장 상태',
      // '출고 대기/진행중' 뱃지가 잘리지 않는 최소 폭
      minSize: 132,
      size: 132,
      cell: ({ row }) => {
        const value = row.original.reportedShippingLabelStatus;
        if (!value) return '-';

        return <StatusBadge value={value} colors={DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS_BADGE_COLOR} />;
      },
    },
    {
      accessorKey: 'reportedDeliveryResults',
      header: '배송결과',
      size: 185,
      cell: ({ row }) => {
        const value = row.original.reportedDeliveryResults;
        if (!value?.length) return null;

        return (
          <div className={styles['delivery-results-cell']}>
            {value.map((result) => (
              <StatusBadge
                key={result.code}
                value={result}
                colors={DELIVERY_AGENCY_REPORTED_DELIVERY_RESULT_BADGE_COLOR}
                dashIfNoLabel={false}
                size="small"
                status={false}
              />
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'deliveryLocation',
      header: '요청 장소',
      size: 76,
    },
    {
      accessorKey: 'deliveryLocationDetail',
      header: '요청 장소 상세',
      size: 170,
      cell: ({ getValue }) => <TooltipTextCell value={getValue()} preserveWhitespace />,
    },
    {
      accessorKey: 'actualDeliveryLocationType',
      header: '처리 장소',
      size: 122,
    },
    {
      accessorKey: 'deliveryFailureReasonCategory',
      header: '배송 실패 사유',
      size: 170,
      cell: ({ row }) => row.original.deliveryFailureReasonCategory?.description || '-',
    },
    {
      accessorKey: 'changedDeliveryMethodType',
      header: '타 장소에 배송한 사유',
      size: 186,
      cell: ({ row }) => row.original.changedDeliveryMethodType?.description || '-',
    },
    {
      accessorKey: 'entranceMethod',
      header: '출입 방법',
      size: 160,
    },
    {
      accessorKey: 'receiverName',
      header: '수령자',
      size: 100,
    },
    {
      accessorKey: 'refineFullAddress',
      header: '정제 주소',
      minSize: 252,
      meta: { flex: true },
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyReportsColumnDefs;
